import { Expose, Exclude, Transform } from 'class-transformer'
import { DocumentSnapshot } from 'firebase/firestore'
import { _FirebaseDocument, anyToDate } from './Models'

export type CommandStatus = 'requested' | 'in_progress' | 'done' | 'failed'

/**
 * Payload for update_process_list command
 */
@Exclude()
export class UpdateProcessListPayload {
  @Expose()
  public kind: 'update_process_list' = 'update_process_list'

  @Expose()
  public available_standard_id: string | null = null

  @Expose()
  public result: UpdateProcessListResult | null = null
}

/**
 * Result returned when update_process_list completes successfully
 */
@Exclude()
export class UpdateProcessListResult {
  @Expose()
  public process_list_memory_id: string | null = null

  @Expose()
  public deleted: number = 0

  @Expose()
  public created: number = 0

  @Expose()
  public skipped: number = 0
}

/**
 * Payload for extract_regulations_list command queue
 */
@Exclude()
export class ExtractRegulationsListPayload {
  @Expose()
  public kind: 'extract_regulations_list' = 'extract_regulations_list'

  @Expose()
  public document_id: string | null = null

  @Expose()
  public datasource_id: string | null = null

  @Expose()
  public result: ExtractRegulationsListResult | null = null
}

@Exclude()
export class ExtractRegulationsListResult {
  @Expose()
  public regulations_count: number = 0

  @Expose()
  public list_document_id: string | null = null
}

/**
 * Payload for regulation_watch command queue
 */
@Exclude()
export class RegulationWatchPayload {
  @Expose()
  public kind: 'regulation_watch' = 'regulation_watch'

  @Expose()
  public reference_date: string | null = null

  @Expose()
  public result: RegulationWatchResult | null = null
}

@Exclude()
export class RegulationWatchResult {
  @Expose()
  public processed: number = 0

  @Expose()
  public skipped_cache: number = 0

  @Expose()
  public failed: number = 0

  @Expose()
  public report_id: string | null = null
}

/**
 * Generic command document for command_queues
 */
@Exclude()
export class Command extends _FirebaseDocument {
  @Expose()
  public status: CommandStatus = 'requested'

  @Expose()
  public payload: Record<string, unknown> = {}

  @Expose()
  public requested_by: string | null = null

  @Expose()
  public error: string | null = null

  @Expose()
  @Transform(anyToDate)
  public started_at: Date | null = null

  @Expose()
  @Transform(anyToDate)
  public finished_at: Date | null = null

  get isRequested(): boolean {
    return this.status === 'requested'
  }

  get isInProgress(): boolean {
    return this.status === 'in_progress'
  }

  get isDone(): boolean {
    return this.status === 'done'
  }

  get isFailed(): boolean {
    return this.status === 'failed'
  }

  get isPending(): boolean {
    return this.isRequested || this.isInProgress
  }

  get isComplete(): boolean {
    return this.isDone || this.isFailed
  }

  collection(): string {
    return 'commands'
  }

  static fromFirebase(o: DocumentSnapshot): Command | null {
    return _FirebaseDocument._fromFirebase(Command, o)
  }
}
